import { useState, useMemo } from 'react';

export interface UsePaginatorOptions {
  itemsPerPage: number;
  defaultPage?: number;
}

export interface PaginatorControls<T> {
  currentPage: number;
  pageCount: number;
  paginatedItems: T[];
  setPage: (page: number) => void;
}

export function usePaginator<T>(
  items: T[],
  { itemsPerPage, defaultPage = 1 }: UsePaginatorOptions
): PaginatorControls<T> {
  const [currentPage, setCurrentPage] = useState(defaultPage);

  const pageCount = useMemo(
    () => Math.ceil(items.length / itemsPerPage),
    [items.length, itemsPerPage]
  );

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, itemsPerPage]);

  return useMemo(
    () => ({
      currentPage,
      pageCount,
      paginatedItems,
      setPage: setCurrentPage,
    }),
    [currentPage, pageCount, paginatedItems]
  );
}
